import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Card,
  Text,
  Button,
  InlineStack,
  BlockStack,
  Icon,
  Collapsible,
  Badge,
  SkeletonDisplayText,
  SkeletonBodyText,
} from "@shopify/polaris";
import DataTableSkeleton from "components/datatable/DataTableSkeleton";
import {
  PlusIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@shopify/polaris-icons";
import { AppDispatch, RootState } from "configureStore";
import { setSelectedDelivery } from "features/deliveries/deliveries.slice";
import { setSelectedReservation } from "features/reservations/reservations.slice";
import {
  setModalxComponent,
  setModalxComponentProps,
} from "components/modalx/modalx.slice";
import { fetchOrderDetails } from "containers/order-view-container/order-container.slice";
import { Delivery, DeliveryStatus } from "types/Delivery";
import { Reservation, ReservationStatus } from "services/reservations.service";
import DeliveriesTable from "features/deliveries/components/DeliveriesTable";
import ReservationCard from "features/reservations/components/ReservationCard";
import { buildFulfillmentSummary } from "../fulfillmentSummary";
import { summaryRowTitle } from "../orderLineRows";
import { summaryRowStatus } from "../summaryRowStatus";
import useIsPhone from "features/files/use-is-phone";
import FulfillmentSummaryCards from "./FulfillmentSummaryCards";

interface FulfillmentSectionProps {
  orderId: string;
  companyId: string;
}

// Status badge helpers
const getDeliveryStatusBadgeTone = (
  status: DeliveryStatus
): "info" | "success" | "warning" | "critical" | "attention" => {
  switch (status) {
    case "draft":
      return "info";
    case "planned":
      return "attention";
    case "shipped":
      return "warning";
    case "delivered":
      return "success";
    case "cancelled":
      return "critical";
    default:
      return "info";
  }
};

const getReservationStatusBadgeTone = (
  status: ReservationStatus
): "info" | "success" | "warning" | "critical" | "attention" => {
  switch (status) {
    case "draft":
      return "info";
    case "confirmed":
      return "success";
    case "cancelled":
      return "critical";
    default:
      return "info";
  }
};

export default function FulfillmentSection({
  orderId,
  companyId,
}: FulfillmentSectionProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const isPhone = useIsPhone();

  // Collapsible states
  const [reservationsOpen, setReservationsOpen] = useState(true);
  const [deliveriesOpen, setDeliveriesOpen] = useState(true);

  // Get order entity with reservations and deliveries from order container slice
  const { orderEntity, isLoading } = useSelector(
    (state: RootState) => state.orderContainerReducer
  );

  // Memoize derived data from orderEntity to prevent useMemo dependency issues
  const reservations = useMemo(
    () => (orderEntity?.reservations || []) as Reservation[],
    [orderEntity?.reservations]
  );
  const deliveries = useMemo(
    () => (orderEntity?.deliveries || []) as Delivery[],
    [orderEntity?.deliveries]
  );
  // Trucks newest first: terminalas rows carry the actual load time
  // (loadedAt), manual rows fall back to deliveryDate, and anything still
  // missing both falls back to when the row was created.
  const sortedDeliveries = useMemo(() => {
    const timeOf = (delivery: Delivery) => {
      const raw = delivery.loadedAt ?? delivery.deliveryDate ?? delivery.createdAt;
      const time = raw ? new Date(raw).getTime() : NaN;
      return Number.isNaN(time) ? 0 : time;
    };
    return [...deliveries].sort((a, b) => timeOf(b) - timeOf(a));
  }, [deliveries]);
  const orderItems = useMemo(
    () => orderEntity?.items || [],
    [orderEntity?.items]
  );

  const refreshData = () => {
    if (companyId && orderId) {
      dispatch(fetchOrderDetails({ companyId, orderId }));
    }
  };

  const productGroupLines = useMemo(
    () => orderEntity?.productGroups || [],
    [orderEntity?.productGroups]
  );

  const fulfillmentSummary = useMemo(
    () =>
      buildFulfillmentSummary(
        orderItems,
        productGroupLines,
        reservations,
        deliveries
      ),
    [orderItems, productGroupLines, reservations, deliveries]
  );

  // Handler for creating reservation with pre-selected product
  const handleCreateReservation = (productId?: string) => {
    dispatch(setModalxComponent("ReservationForm"));
    dispatch(
      setModalxComponentProps({
        orderId,
        mode: "create",
        preSelectedProductId: productId,
      })
    );
  };

  // Handler for creating delivery with pre-selected product filter
  const handleCreateDelivery = (productId?: string) => {
    dispatch(setModalxComponent("DeliveryForm"));
    dispatch(
      setModalxComponentProps({
        orderId,
        mode: "create",
        filterByProductId: productId,
      })
    );
  };

  // Handler for editing reservation
  const handleEditReservation = (reservation: Reservation) => {
    dispatch(setSelectedReservation(reservation));
    dispatch(setModalxComponent("ReservationForm"));
    dispatch(
      setModalxComponentProps({
        orderId,
        reservationId: reservation.id,
        mode: "edit",
      })
    );
  };

  // Handler for editing delivery
  const handleEditDelivery = (delivery: Delivery) => {
    dispatch(setSelectedDelivery(delivery));
    dispatch(setModalxComponent("DeliveryForm"));
    dispatch(
      setModalxComponentProps({
        orderId,
        deliveryId: delivery.id,
        mode: "edit",
      })
    );
  };

  if (isLoading) {
    // Skeleton mirroring the fulfillment layout (header, summary table, and the
    // reservations + deliveries cards) instead of a bare spinner.
    return (
      <BlockStack gap="400">
        <Card>
          <SkeletonBodyText lines={1} />
        </Card>
        <Card>
          <BlockStack gap="300">
            <SkeletonDisplayText size="small" />
            <DataTableSkeleton rows={4} columns={7} />
          </BlockStack>
        </Card>
        <Card>
          <BlockStack gap="300">
            <SkeletonDisplayText size="small" />
            <SkeletonBodyText lines={2} />
          </BlockStack>
        </Card>
        <Card>
          <BlockStack gap="300">
            <SkeletonDisplayText size="small" />
            <SkeletonBodyText lines={2} />
          </BlockStack>
        </Card>
      </BlockStack>
    );
  }

  // Count stats
  const confirmedReservations = reservations.filter(
    (r) => r.status === "confirmed"
  ).length;
  const draftReservations = reservations.filter(
    (r) => r.status === "draft"
  ).length;
  const shippedDeliveries = deliveries.filter(
    (d) => d.status === "shipped" || d.status === "delivered"
  ).length;
  const pendingDeliveries = deliveries.filter(
    (d) => d.status === "draft" || d.status === "planned"
  ).length;

  // Build badge text for collapsible headers
  const reservationsBadgeText = draftReservations > 0 
    ? `${confirmedReservations} ${t("confirmed")} / ${draftReservations} ${t("draft")}`
    : `${confirmedReservations} ${t("confirmed")}`;
  
  const deliveriesBadgeText = pendingDeliveries > 0
    ? `${shippedDeliveries} ${t("shipped")} / ${pendingDeliveries} ${t("pending")}`
    : `${shippedDeliveries} ${t("shipped")}`;

  return (
    <BlockStack gap="400">
      {/* Header with quick action buttons */}
      <Card>
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingSm">
            {t("fulfillment")}
          </Text>
          <InlineStack gap="200">
            <Button onClick={() => handleCreateReservation()}>
              <Icon source={PlusIcon as any} />
              {t("reserve")}
            </Button>
            <Button onClick={() => handleCreateDelivery()} variant="primary">
              <Icon source={PlusIcon as any} />
              {t("ship")}
            </Button>
          </InlineStack>
        </InlineStack>
      </Card>

      {/* Unified Summary Table */}
      {(orderItems.length > 0 || productGroupLines.length > 0) && (
        <Card>
          <BlockStack gap="300">
            <Text as="h3" variant="headingSm">
              {t("fulfillment_summary")}
            </Text>
            {isPhone ? (
              <FulfillmentSummaryCards
                rows={fulfillmentSummary}
                onReserve={handleCreateReservation}
                onShip={handleCreateDelivery}
              />
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "14px",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        borderBottom: "1px solid #e1e3e5",
                        textAlign: "left",
                      }}
                    >
                      <th style={{ padding: "12px 8px", fontWeight: 600 }}>
                        {t("sku")}
                      </th>
                      <th style={{ padding: "12px 8px", fontWeight: 600 }}>
                        {t("product_name")}
                      </th>
                      <th
                        style={{
                          padding: "12px 8px",
                          fontWeight: 600,
                          textAlign: "right",
                        }}
                      >
                        {t("ordered")}
                      </th>
                      <th
                        style={{
                          padding: "12px 8px",
                          fontWeight: 600,
                          textAlign: "right",
                        }}
                      >
                        {t("reserved")}
                      </th>
                      <th
                        style={{
                          padding: "12px 8px",
                          fontWeight: 600,
                          textAlign: "right",
                        }}
                      >
                        {t("pending")}
                      </th>
                      <th
                        style={{
                          padding: "12px 8px",
                          fontWeight: 600,
                          textAlign: "right",
                        }}
                      >
                        {t("shipped")}
                      </th>
                      <th
                        style={{
                          padding: "12px 8px",
                          fontWeight: 600,
                          textAlign: "center",
                        }}
                      >
                        {t("actions")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {fulfillmentSummary.map((item) => {
                      const status = summaryRowStatus(item);
                      return (
                        <tr
                          key={item.key}
                          style={{ borderBottom: "1px solid #f1f2f3" }}
                        >
                          <td style={{ padding: "12px 8px" }}>{item.sku}</td>
                          <td style={{ padding: "12px 8px" }}>
                            {summaryRowTitle(item, t)}
                          </td>
                          <td style={{ padding: "12px 8px", textAlign: "right" }}>
                            {item.ordered}
                          </td>
                          <td style={{ padding: "12px 8px", textAlign: "right" }}>
                            <Badge tone={status.reservedTone}>
                              {status.reservedText}
                            </Badge>
                          </td>
                          <td style={{ padding: "12px 8px", textAlign: "right" }}>
                            {status.pendingText ? (
                              <Badge tone="info">{status.pendingText}</Badge>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td style={{ padding: "12px 8px", textAlign: "right" }}>
                            <span
                              title={
                                status.overshootTextKey
                                  ? t(status.overshootTextKey)
                                  : undefined
                              }
                            >
                              <Badge tone={status.shippedTone}>
                                {status.shippedText}
                              </Badge>
                            </span>
                          </td>
                          <td style={{ padding: "12px 8px", textAlign: "center" }}>
                            <InlineStack gap="100" align="center">
                              {status.canReserve && (
                                <Button
                                  size="slim"
                                  onClick={() =>
                                    handleCreateReservation(item.productId)
                                  }
                                >
                                  {`+${t("reserve")}`}
                                </Button>
                              )}
                              {status.canShip && (
                                <Button
                                  size="slim"
                                  variant="primary"
                                  onClick={() =>
                                    handleCreateDelivery(item.productId)
                                  }
                                >
                                  {`+${t("ship")}`}
                                </Button>
                              )}
                              {status.isComplete && (
                                <Badge tone="success">{t("complete")}</Badge>
                              )}
                            </InlineStack>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </BlockStack>
        </Card>
      )}

      {/* Reservations Section - Collapsible */}
      <Card>
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="center">
            <InlineStack gap="200" blockAlign="center">
              <Button
                variant="plain"
                onClick={() => setReservationsOpen(!reservationsOpen)}
                icon={reservationsOpen ? (ChevronUpIcon as any) : (ChevronDownIcon as any)}
              >
                {t("reservations")}
              </Button>
              <Badge tone="info">{reservationsBadgeText}</Badge>
            </InlineStack>
            <Button
              size="slim"
              onClick={() => handleCreateReservation()}
              icon={PlusIcon as any}
            >
              {t("new")}
            </Button>
          </InlineStack>

          <Collapsible
            open={reservationsOpen}
            id="reservations-collapsible"
            transition={{ duration: "200ms", timingFunction: "ease-in-out" }}
          >
            {reservations.length === 0 ? (
              <Text as="p" tone="subdued">
                {t("reservations_no_reservations")}
              </Text>
            ) : (
              <BlockStack gap="200">
                {reservations
                  .filter((r) => r && r.id)
                  .map((reservation) => (
                    <ReservationCard
                      key={reservation.id}
                      reservation={reservation}
                      onEdit={() => handleEditReservation(reservation)}
                      onRefresh={refreshData}
                    />
                  ))}
              </BlockStack>
            )}
          </Collapsible>
        </BlockStack>
      </Card>

      {/* Deliveries Section - Collapsible */}
      <Card>
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="center">
            <InlineStack gap="200" blockAlign="center">
              <Button
                variant="plain"
                onClick={() => setDeliveriesOpen(!deliveriesOpen)}
                icon={deliveriesOpen ? (ChevronUpIcon as any) : (ChevronDownIcon as any)}
              >
                {t("deliveries")}
              </Button>
              <Badge tone="success">{deliveriesBadgeText}</Badge>
            </InlineStack>
            <Button
              size="slim"
              variant="primary"
              onClick={() => handleCreateDelivery()}
              icon={PlusIcon as any}
            >
              {t("new")}
            </Button>
          </InlineStack>

          <Collapsible
            open={deliveriesOpen}
            id="deliveries-collapsible"
            transition={{ duration: "200ms", timingFunction: "ease-in-out" }}
          >
            {deliveries.length === 0 ? (
              <Text as="p" tone="subdued">
                {t("deliveries_no_deliveries")}
              </Text>
            ) : (
              <DeliveriesTable
                deliveries={sortedDeliveries}
                onEdit={handleEditDelivery}
                onRefresh={refreshData}
              />
            )}
          </Collapsible>
        </BlockStack>
      </Card>
    </BlockStack>
  );
}

export { getDeliveryStatusBadgeTone, getReservationStatusBadgeTone };
